/**
 * 幻灯片展示组件
 *
 * 以全屏覆盖层形式展示照片，支持：
 * - 自动播放（可配置间隔：3s/5s/10s）
 * - 暂停/继续控制
 * - 全屏模式（Fullscreen API）
 * - 淡入淡出过渡动画
 * - 键盘导航（左右方向键切换，ESC 退出）
 * - 触摸滑动手势（移动端）
 * - 照片计数器（x/n）
 */
import { useState, useCallback, useEffect, useRef } from 'react';

import type { ShareResponse } from '@photo-process/shared';

import styles from './Slideshow.module.css';

/** 单张照片类型（从 ShareResponse 提取） */
type SharePhoto = ShareResponse['photos'][number];

/** 自动播放间隔选项（秒） */
type IntervalOption = 3 | 5 | 10;

/** 可用的间隔选项列表 */
const INTERVAL_OPTIONS: readonly IntervalOption[] = [3, 5, 10] as const;

/** 触摸滑动最小距离阈值（像素） */
const SWIPE_THRESHOLD = 50;

/** Slideshow 组件属性 */
interface SlideshowProps {
  /** 照片列表（不可变数组） */
  readonly photos: readonly SharePhoto[];
  /** 初始显示的照片索引 */
  readonly initialIndex?: number;
  /** 关闭幻灯片的回调 */
  readonly onClose: () => void;
}

/**
 * 幻灯片展示组件
 * 全屏覆盖层展示照片，支持自动播放、全屏和手势操作
 */
export function Slideshow({
  photos,
  initialIndex = 0,
  onClose,
}: SlideshowProps): React.JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(false);
  const [interval, setInterval_] = useState<IntervalOption>(5);
  const [isVisible, setIsVisible] = useState(true);

  // 引用，用于全屏和触摸手势
  const overlayRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);

  // 切换到上一张照片（带淡入淡出）
  const goToPrevious = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentIndex((prev) =>
        prev === 0 ? photos.length - 1 : prev - 1,
      );
      setIsVisible(true);
    }, 250);
  }, [photos.length]);

  // 切换到下一张照片（带淡入淡出）
  const goToNext = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentIndex((prev) =>
        prev === photos.length - 1 ? 0 : prev + 1,
      );
      setIsVisible(true);
    }, 250);
  }, [photos.length]);

  // 切换播放/暂停
  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  // 切换全屏模式
  const toggleFullscreen = useCallback(() => {
    const element = overlayRef.current;
    if (!element) return;

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void element.requestFullscreen();
    }
  }, []);

  // 更新播放间隔
  const handleIntervalChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setInterval_(Number(e.target.value) as IntervalOption);
    },
    [],
  );

  // 自动播放定时器
  useEffect(() => {
    if (!isPlaying || photos.length <= 1) return;

    const timer = window.setInterval(() => {
      goToNext();
    }, interval * 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [isPlaying, interval, goToNext, photos.length]);

  // 键盘导航
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          goToNext();
          break;
        case 'Escape':
          e.preventDefault();
          // 如果在全屏模式，先退出全屏；否则关闭幻灯片
          if (document.fullscreenElement) {
            void document.exitFullscreen();
          } else {
            onClose();
          }
          break;
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPrevious, goToNext, onClose, togglePlay]);

  // 触摸手势处理
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - touchStartXRef.current;
      const deltaY = touch.clientY - touchStartYRef.current;

      // 仅水平方向滑动有效（水平位移 > 垂直位移）
      if (
        Math.abs(deltaX) > SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        if (deltaX > 0) {
          goToPrevious();
        } else {
          goToNext();
        }
      }
    },
    [goToPrevious, goToNext],
  );

  // 空照片列表时的提示
  if (photos.length === 0) {
    return (
      <div className={styles.overlay}>
        <p style={{ color: '#999' }}>暂无照片</p>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="关闭"
        >
          &times;
        </button>
      </div>
    );
  }

  const currentPhoto = photos[currentIndex];
  if (!currentPhoto) {
    return (
      <div className={styles.overlay}>
        <p style={{ color: '#999' }}>照片加载异常</p>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="关闭"
        >
          &times;
        </button>
      </div>
    );
  }

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      role="dialog"
      aria-label="幻灯片播放"
      aria-modal="true"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* 关闭按钮 */}
      <button
        type="button"
        className={styles.closeButton}
        onClick={onClose}
        aria-label="关闭幻灯片"
      >
        &times;
      </button>

      {/* 照片展示区域 */}
      <div className={styles.photoContainer}>
        {/* 上一张按钮 */}
        {photos.length > 1 && (
          <button
            type="button"
            className={`${styles.navButton} ${styles.navPrev}`}
            onClick={goToPrevious}
            aria-label="上一张"
          >
            &#8249;
          </button>
        )}

        {/* 当前照片 */}
        <img
          className={`${styles.photo} ${isVisible ? styles.photoVisible : styles.photoHidden}`}
          src={currentPhoto.url}
          alt={currentPhoto.filename}
          draggable={false}
        />

        {/* 下一张按钮 */}
        {photos.length > 1 && (
          <button
            type="button"
            className={`${styles.navButton} ${styles.navNext}`}
            onClick={goToNext}
            aria-label="下一张"
          >
            &#8250;
          </button>
        )}
      </div>

      {/* 底部控制栏 */}
      <div className={styles.controls}>
        {/* 播放/暂停按钮 */}
        <button
          type="button"
          className={styles.controlButton}
          onClick={togglePlay}
          aria-label={isPlaying ? '暂停自动播放' : '开始自动播放'}
        >
          {isPlaying ? '暂停' : '播放'}
        </button>

        {/* 间隔选择 */}
        <select
          className={styles.intervalSelect}
          value={interval}
          onChange={handleIntervalChange}
          aria-label="播放间隔"
        >
          {INTERVAL_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}s
            </option>
          ))}
        </select>

        {/* 照片计数 */}
        <span className={styles.counter}>
          {currentIndex + 1} / {photos.length}
        </span>

        {/* 全屏按钮 */}
        <button
          type="button"
          className={styles.controlButton}
          onClick={toggleFullscreen}
          aria-label="切换全屏"
        >
          全屏
        </button>
      </div>
    </div>
  );
}
